class ErrorBoundaryService {
  constructor() {
    this.errors = [];
    this.maxErrors = 100;
    this.listeners = [];
  }

  logError(error, context = {}) {
    const errorEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      message: error.message || 'Unknown error',
      stack: error.stack,
      name: error.name,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    this.errors.unshift(errorEntry);

    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    this.notifyListeners(errorEntry);

    if (process.env.NODE_ENV === 'development') {
      console.error('Error logged:', errorEntry);
    }

    this.reportError(errorEntry);

    return errorEntry.id;
  }

  reportError(errorEntry) {
    if (process.env.NODE_ENV === 'production') {
      try {
        fetch('/api/errors', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(errorEntry)
        }).catch(err => {
          console.warn('Failed to report error to server:', err);
        });
      } catch (err) {
        console.warn('Failed to send error report:', err);
      }
    }
  }

  addListener(listener) {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  notifyListeners(errorEntry) {
    this.listeners.forEach(listener => {
      try {
        listener(errorEntry);
      } catch (err) {
        console.error('Error in error listener:', err);
      }
    });
  }

  getErrors(limit = 20) {
    return this.errors.slice(0, limit);
  }

  clearErrors() {
    this.errors = [];
  }

  createErrorHandler(context = {}) {
    return (error) => {
      this.logError(error, context);
    };
  }

  handlePromiseRejection(event) {
    this.logError(event.reason || new Error('Unhandled promise rejection'), {
      type: 'unhandledRejection',
      promise: 'Promise rejection'
    });
  }

  handleGlobalError(event) {
    this.logError(event.error || new Error(event.message), {
      type: 'globalError',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    });
  }

  setupGlobalHandlers() {
    // Store bound handlers for proper cleanup
    this._boundHandleGlobalError = this.handleGlobalError.bind(this);
    this._boundHandlePromiseRejection = this.handlePromiseRejection.bind(this);
    this._originalConsoleError = console.error;

    window.addEventListener('error', this._boundHandleGlobalError);
    window.addEventListener('unhandledrejection', this._boundHandlePromiseRejection);

    console.error = (...args) => {
      const error = args[0];
      if (error instanceof Error) {
        this.logError(error, {
          type: 'consoleError',
          arguments: args.slice(1)
        });
      }
      this._originalConsoleError.apply(console, args);
    };

    // Set up periodic memory cleanup
    this._cleanupInterval = setInterval(() => {
      this.cleanupOldErrors();
    }, 5 * 60 * 1000); // Clean every 5 minutes
  }

  removeGlobalHandlers() {
    if (this._boundHandleGlobalError) {
      window.removeEventListener('error', this._boundHandleGlobalError);
      this._boundHandleGlobalError = null;
    }

    if (this._boundHandlePromiseRejection) {
      window.removeEventListener('unhandledrejection', this._boundHandlePromiseRejection);
      this._boundHandlePromiseRejection = null;
    }

    if (this._originalConsoleError) {
      console.error = this._originalConsoleError;
      this._originalConsoleError = null;
    }

    if (this._cleanupInterval) {
      clearInterval(this._cleanupInterval);
      this._cleanupInterval = null;
    }
  }

  cleanupOldErrors() {
    const maxAge = 30 * 60 * 1000; // 30 minutes
    const now = Date.now();

    this.errors = this.errors.filter(error => {
      const age = now - new Date(error.timestamp).getTime();
      return age < maxAge;
    });
  }

  isNetworkError(error) {
    return error.message?.includes('Network Error') ||
           error.code === 'NETWORK_ERROR' ||
           error.name === 'NetworkError';
  }

  isAuthError(error) {
    return error.response?.status === 401 ||
           error.response?.status === 403;
  }

  shouldRetry(error) {
    if (this.isAuthError(error)) return false;
    if (error.response?.status >= 400 && error.response?.status < 500) return false;
    return true;
  }

  getRetryDelay(attempt) {
    return Math.min(1000 * Math.pow(2, attempt), 30000);
  }

  async withRetry(fn, maxAttempts = 3, context = {}) {
    let lastError;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;

        this.logError(error, {
          ...context,
          attempt,
          maxAttempts,
          retrying: attempt < maxAttempts
        });

        if (attempt === maxAttempts || !this.shouldRetry(error)) {
          throw error;
        }

        const delay = this.getRetryDelay(attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError;
  }
}

const errorBoundaryService = new ErrorBoundaryService();

export const logError = (error, context) => errorBoundaryService.logError(error, context);
export const addErrorListener = (listener) => errorBoundaryService.addListener(listener);
export const getErrors = (limit) => errorBoundaryService.getErrors(limit);
export const clearErrors = () => errorBoundaryService.clearErrors();
export const createErrorHandler = (context) => errorBoundaryService.createErrorHandler(context);
export const withRetry = (fn, maxAttempts, context) => errorBoundaryService.withRetry(fn, maxAttempts, context);
export const setupGlobalHandlers = () => errorBoundaryService.setupGlobalHandlers();
export const removeGlobalHandlers = () => errorBoundaryService.removeGlobalHandlers();

export default errorBoundaryService;